import { mapToInitials } from "./mapToInitials";
import React, { useEffect, useRef } from "react";
import { Mode, PartialMenu } from "../../../../../interfaces/meMenuInterfaces/interfaces";
import { Option } from "../../../../../interfaces/interfaces";
import { useFormikContext } from "formik";
import { MenuInitialValues } from "./AddEditMenuModal";
import { useTranslation } from "react-i18next";
import { buildCloneInitialDisplayNameWithLimit, CLONE_MENU_NAME_MAX_LENGTH } from "../../../utils/cloneNameUtils";
import { MenuStatusType } from "@me-team/host/main/graphql/types";

interface MenuInitSyncProps {
    mode: Mode;
    menu?: PartialMenu;
    languageVersionOptions: Option[];
    defaultStatusForCreate?: MenuStatusType;
    ensurePrimaryLanguageId?: number;
}

const isBlank = (v: MenuInitialValues) =>
    v.name === "" && v.type == null && (v.languages?.length ?? 0) === 0 && v.status == null;

export const MenuInitSync: React.FC<MenuInitSyncProps> = ({
    mode,
    menu,
    languageVersionOptions,
    defaultStatusForCreate,
    ensurePrimaryLanguageId,
}) => {
    const { t } = useTranslation();
    const { values, setValues, dirty } = useFormikContext<MenuInitialValues>();
    const appliedForKey = useRef<string | null>(null);
    const key = `${mode}-${menu?.id ?? "create"}`;
    const isValuesBlank = isBlank(values);

    useEffect(() => {
        if (!languageVersionOptions?.length) return;
        if (appliedForKey.current === key) return;

        const next = mapToInitials(mode, menu, languageVersionOptions, t, {
            defaultStatusForCreate,
            ensurePrimaryLanguageId,
        });

        if (mode === "clone") {
            next.name = buildCloneInitialDisplayNameWithLimit(menu?.name, t, CLONE_MENU_NAME_MAX_LENGTH);
        }

        if (isValuesBlank || !dirty) {
            setValues(next, false);
            appliedForKey.current = key;
        }
    }, [
        key,
        mode,
        menu,
        t,
        languageVersionOptions,
        defaultStatusForCreate,
        ensurePrimaryLanguageId,
        isValuesBlank,
        dirty,
        setValues,
    ]);

    return null;
};
